<template>
  <section class="moment-panel" :aria-label="suspense ? 'Момент проигрыша скоро откроется' : 'Запись момента проигрыша'">
    <div ref="media" class="moment-panel__media" :class="{ 'moment-panel__media--revealed': !suspense && frameUrl && !unavailable }">
      <div v-if="suspense" class="moment-panel__suspense" aria-hidden="true">
        <span class="moment-panel__arc"></span>
        <span class="moment-panel__question">?</span>
      </div>
      <template v-else-if="frameUrl && !unavailable">
        <img ref="image" class="moment-panel__image" :class="{ 'moment-panel__image--holding': holding }" :style="{ transform: zoomTransform }" :src="frameUrl" alt="Сохранённый кадр подтверждённого нарушения" @load="measure" @error="$emit('image-error')" />
        <span class="moment-panel__label">{{ keyFrameOnly ? 'Ключевой кадр · без записи' : playing ? 'Запись · без звука' : 'Ключевой кадр' }}</span>
      </template>
      <div v-else class="moment-panel__unavailable" role="status">
        <AppIcon name="camera" :size="30" />
        <strong>{{ unavailable ? 'Запись недоступна' : 'Получаем запись' }}</strong>
        <span>{{ unavailable ? 'Результат подтверждён игровой системой' : 'Живая связь продолжается' }}</span>
      </div>
    </div>
    <div class="moment-panel__caption" :class="{ 'moment-panel__caption--hidden': suspense }" :aria-hidden="suspense ? 'true' : undefined">
      <span v-if="reason" class="moment-panel__reason"><span aria-hidden="true"></span>{{ reason }}</span>
      <span class="moment-panel__player cb-ellipsis">{{ loserName }}</span>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import AppIcon from "@/components/ui/AppIcon.vue";
import { replayZoomTransform } from "@/services/postRound";
const props = defineProps({
  suspense: { type: Boolean, default: false },
  frameUrl: { type: String, default: "" },
  playing: { type: Boolean, default: false },
  holding: { type: Boolean, default: false },
  keyFrameOnly: { type: Boolean, default: false },
  zoomProgress: { type: Number, default: 0 },
  region: { type: Object, default: null },
  unavailable: { type: Boolean, default: false },
  loserName: { type: String, default: "" },
  reason: { type: String, default: "" },
});
defineEmits(["image-error"]);
const image = ref(null);
const media = ref(null);
const boxRatio = ref(1);
const imageRatio = ref(1);
let observer;
function measure() {
  if (media.value?.clientHeight) boxRatio.value = media.value.clientWidth / media.value.clientHeight;
  if (image.value?.naturalHeight) imageRatio.value = image.value.naturalWidth / image.value.naturalHeight;
}
const zoomTransform = computed(() => replayZoomTransform(props.region, props.zoomProgress, boxRatio.value, imageRatio.value));
onMounted(() => {
  measure();
  if (typeof ResizeObserver !== "undefined") { observer = new ResizeObserver(measure); observer.observe(media.value); }
});
onBeforeUnmount(() => observer?.disconnect());
</script>

<style scoped>
.moment-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}
.moment-panel__media {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--cb-border-strong);
  border-radius: 16px;
  background: var(--cb-surface);
}
.moment-panel__media--revealed { border: 2px solid var(--cb-danger); }
.moment-panel__image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  transform-origin: center;
  will-change: transform;
}
.moment-panel__image--holding {
  animation: moment-hold 2s ease-in-out infinite;
}
.moment-panel__label {
  position: absolute;
  top: 8px;
  left: 8px;
  max-width: calc(100% - 16px);
  padding: 4px 7px;
  border-radius: 7px;
  background: var(--cb-overlay);
  color: var(--cb-text);
  font-size: 12px;
}
.moment-panel__suspense {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
}
.moment-panel__arc {
  position: absolute;
  width: min(48%, 150px);
  aspect-ratio: 1;
  border: 2px solid var(--cb-border-strong);
  border-top-color: var(--cb-lime);
  border-right-color: var(--cb-lime);
  border-radius: 50%;
  animation: moment-turn 1.6s linear infinite;
}
.moment-panel__question {
  color: var(--cb-lime);
  font-size: clamp(64px, 20vw, 100px);
  font-weight: 800;
  line-height: 1;
}
.moment-panel__unavailable {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 18px;
  color: var(--cb-text-2);
  text-align: center;
  font-size: 13px;
}
.moment-panel__unavailable strong { color: var(--cb-text); font-size: 16px; }
.moment-panel__caption { flex: 0 0 40px; min-width: 0; padding: 0 3px; }
.moment-panel__caption--hidden { visibility: hidden; }
.moment-panel__reason {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--cb-text);
  font-size: 14px;
  font-weight: 600;
}
.moment-panel__reason > span {
  width: 9px;
  height: 9px;
  flex: 0 0 9px;
  border-radius: 50%;
  background: var(--cb-danger);
}
.moment-panel__player { display: block; padding-left: 17px; color: var(--cb-text-2); font-size: 12px; }
@keyframes moment-turn { to { transform: rotate(360deg); } }
@keyframes moment-hold { 0%, 100% { filter: brightness(1); } 50% { filter: brightness(.83); } }
@media (prefers-reduced-motion: reduce) {
  .moment-panel__arc, .moment-panel__image { animation: none; }
  .moment-panel__image { transform: none !important; }
}
</style>
